import { isEmpty } from "lodash";
import { useMemo, useState } from "react";
import toast from "react-hot-toast";
import {
  useCreateAppointmentCommunicationConfigMutation,
  useUpdateAppointmentCommunicationMutation,
} from "@/graphql/mutations/updateAppointmentCommunication.graphql.types";
import useApptCustomizedCommunication, {
  CommunicationState,
} from "@/hooks/appointments/useApptCustomizedCommunication";
import { AppointmentStatus } from "@/types";
import useDefaultCommunicationConfig from "./useDefaultCommunicationConfig";

export function useEditApptComms(
  appointmentId: string,
  invoiceId: string,
  appointmentStatus: AppointmentStatus
) {
  const [
    isOverrideCommunicationModalOpen,
    setIsOverrideCommunicationModalOpen,
  ] = useState(false);

  const { defaultConfiguration, hasOverride } = useDefaultCommunicationConfig(
    appointmentId,
    invoiceId,
    isOverrideCommunicationModalOpen
  );

  const [updateAppointmentCommunication] =
    useUpdateAppointmentCommunicationMutation();
  const [createAppointmentCommunication] =
    useCreateAppointmentCommunicationConfigMutation();

  const {
    apptCommunicationState,
    setApptCommunicationState,
    configurationToUpdate,
  } = useApptCustomizedCommunication(defaultConfiguration);
  const editableChannels = useMemo(() => {
    const editableChannels = {} as Record<keyof CommunicationState, boolean>;

    Object.keys(apptCommunicationState).forEach((key) => {
      // Specific stage-based rules
      switch (key as keyof CommunicationState) {
        case "sendBookingConfirmation":
          editableChannels[key] = false;
          break;
        case "sendConfirmationReminder":
          editableChannels[key] =
            appointmentStatus === AppointmentStatus.SCHEDULED;
          break;
        case "sendSecondReminder":
        case "sendCheckInReminder":
          editableChannels[key] = [
            AppointmentStatus.SCHEDULED,
            AppointmentStatus.CONFIRMED,
          ].includes(appointmentStatus);
          break;
        default:
          editableChannels[key] = true;
      }
    });

    return editableChannels;
  }, [apptCommunicationState, appointmentStatus]);

  const handleSaveCommunicationOverride = async () => {
    setIsOverrideCommunicationModalOpen(false);
    if (isEmpty(configurationToUpdate)) {
      return;
    }

    const mutation = hasOverride
      ? updateAppointmentCommunication
      : createAppointmentCommunication;

    await toast.promise(
      mutation({
        variables: {
          appointmentId,
          invoiceId,
          ...configurationToUpdate,
        },
      }),
      {
        loading: "Updating client comms for appointment...",
        success: "Client comms updated!",
        error: "Failed to update client comms for this appointment",
      }
    );
  };

  return {
    isOverrideCommunicationModalOpen,
    setIsOverrideCommunicationModalOpen,
    apptCommunicationState,
    setApptCommunicationState,
    editableChannels,
    handleSaveCommunicationOverride,
  };
}
